// Shared pieces of the engagement workspace tabs. Every act is one catalogue command through the inbox (ActionDrawer or
// useEngine); what the signed-in person may ask for comes from the catalogue (mayRequest), never widened here. A button the
// person's role cannot use is disabled with the catalogue's reason; a command that runs only on the engine's machine is shown
// as an instruction; a control the engine has no command for is shown disabled with "engine support needed".
import { useState, type ReactNode } from "react";
import { ActionDrawer, type ActionIntent } from "../../app/ActionDrawer";
import { useApp } from "../../app/context";
import { useEngine } from "../../app/engine";
import { AgentChip, Button, Card, DocView, EmptyState, ErrorState, LoadState, SourceList, StatusPill } from "../../components";
import type { Action, DocModel, EngagementTables, ToFill } from "../../data/types";
import { actionPayload, addressedTo, mayRequest, payloadFromRun } from "../../lib/commands";
import { titleCase } from "../../lib/format";
import { useLoad, useRealtime } from "../../lib/hooks";
import { useEngagement } from "./Layout";

export type Open = (command: string, title: string, fixed?: Record<string, unknown>, initial?: Record<string, unknown>, toFill?: ToFill[]) => void;

/** One ActionDrawer per page: `open(command, title, fixed)` shows the command's schema form; `drawer` is rendered once. */
export function useAct(onSent?: () => void): { open: Open; drawer: ReactNode } {
  const { id } = useEngagement();
  const [intent, setIntent] = useState<ActionIntent | null>(null);
  const open: Open = (command, title, fixed, initial, toFill) => setIntent({ command, engagementId: id, fixed, initial, title, toFill });
  return { open, drawer: <ActionDrawer intent={intent} onClose={() => setIntent(null)} onSent={onSent} /> };
}

/** May the signed-in person ask for this engagement command? (the catalogue's role and route; the engine decides in the end) */
export function useAllowed() {
  const { catalogue, me } = useApp();
  return (command: string) => {
    const spec = catalogue.get(command);
    return { ...mayRequest(spec, me, "engagement"), spec, local: spec?.route === "local" };
  };
}

/** Send a complete payload straight to the inbox (no form): used where the engine's action already names every input. */
export function useSend() {
  const { id } = useEngagement();
  const request = useEngine();
  return (command: string, payload: Record<string, unknown>, what: string) => request({ command, engagementId: id, payload, what });
}

/** A button that opens a command's form. Disabled with the reason when the person may not ask for it; a local-only command
 * is shown as the line to run on the engine's machine. */
export function Act({ command, title, open, fixed, initial, label, kind = "secondary", size = "sm", reason, toFill }: {
  command: string; title: string; open: Open; fixed?: Record<string, unknown>; initial?: Record<string, unknown>; label?: ReactNode;
  kind?: "primary" | "secondary" | "ghost" | "danger"; size?: "sm" | "md"; reason?: string | null; toFill?: ToFill[];
}) {
  const allowed = useAllowed()(command);
  if (allowed.local) return <span className="font-mono text-[11px] text-muted" title="Runs only on the engine's machine">yvc {command.replace(".", " ")}</span>;
  const why = reason ?? (allowed.ok ? null : allowed.reason);
  const text = label ?? `${titleCase(command.split(".").pop() ?? command)}…`;
  return (
    <Button kind={kind} size={size} disabled={!!why} title={why ?? `${command}${allowed.spec?.humanOnly ? " — your own act" : ""}`}
      aria-label={typeof text === "string" ? `${text}${why ? ` (not available: ${why})` : ""}` : undefined}
      data-command={command} onClick={() => open(command, title, fixed, initial, toFill)}>
      {text}
    </Button>
  );
}

/** A control the engine has no command for yet: visible, disabled, and saying so (listed in docs as an engine gap). */
export function EngineGap({ label, need }: { label: string; need: string }) {
  return (
    <Button size="sm" disabled title={`Engine support needed: ${need}`} aria-label={`${label} (engine support needed: ${need})`}>
      {label}<span className="sr-only"> — engine support needed</span>
    </Button>
  );
}

/** A note where the engine does not publish a read model the screen needs (shown, never faked). */
export function NotPublished({ what, command }: { what: string; command?: string }) {
  return (
    <p className="rounded-md border border-dashed border-line px-3 py-2 text-xs text-muted">
      Not published by the engine yet: {what}{command ? <> (read command <span className="font-mono">{command}</span>)</> : null}. Engine support needed.
    </p>
  );
}

/** The person's own words: engine actions with the command that does them. */
export function QueueList({ actions, open, empty = "Nothing in the engine's queue here." }: { actions: Action[]; open: Open; empty?: string }) {
  const { catalogue, me } = useApp();
  if (!actions.length) return <p className="text-sm text-muted">{empty}</p>;
  return (
    <ul className="divide-y divide-line">
      {actions.map((a) => {
        const to = addressedTo(a.who, me.personName, me.role);
        return (
          <li key={a.key} className="flex flex-wrap items-start gap-2 py-2 text-sm">
            <span className="min-w-0 flex-1">
              <span className={a.byAgent ? "text-agent" : undefined}>{a.what}</span>
              <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted">
                <span className="font-mono">{a.command}</span>
                {a.human ? <span>· a person's act{a.who ? ` · ${a.who}` : ""}</span> : <><span>·</span><AgentChip name="can do this" /></>}
                {to === "me" && <StatusPill state="open" label="For you" />}
                {a.byAgent && <AgentChip name="drafted" />}
                {a.template && !a.template.complete && <span>· needs {a.template.toFill.filter((f) => f.required).map((f) => f.field).join(", ") || "input"} from you</span>}
              </span>
              {a.sources.length > 0 && <span className="mt-1 flex flex-wrap items-center gap-1 text-xs"><span className="text-muted">Rests on</span><SourceList sources={a.sources} /></span>}
            </span>
            <Act command={a.command} title={a.what} open={open} fixed={actionPayload(a, catalogue.get(a.command))} toFill={a.template?.toFill} label="Open form…" />
          </li>
        );
      })}
    </ul>
  );
}

/** A document read model by key from one of the document tables (planning, completion, areas). */
export function useDocs(table: keyof Pick<EngagementTables, "planning" | "completion" | "areas" | "time_summary">) {
  const { id } = useEngagement();
  const { ds } = useApp();
  return useLoad(() => ds.table(id, table), [ds, id, table]);
}

/** A card for one document read model: an engine failure shows as a failure, a missing one says so, else the renderer. */
export function DocCard({ docs, k, title, children, action, empty }: {
  docs: DocModel[] | undefined; k: string; title: ReactNode; children?: (doc: Record<string, unknown>) => ReactNode; action?: ReactNode; empty?: string;
}) {
  const d = docs?.find((x) => x.kind === k);
  return (
    <Card title={title} action={action}>
      {!docs ? <p className="text-sm text-muted">Loading…</p>
        : !d ? <EmptyState title={empty ?? `The engine has not published ${k} for this engagement.`} />
        : d.error ? <ErrorState error={`The engine could not compute ${k}: ${d.error}`} />
        : d.doc === null || d.doc === undefined ? <EmptyState title="Not recorded yet." />
        : children && typeof d.doc === "object" && !Array.isArray(d.doc) ? children(d.doc as Record<string, unknown>) : <DocView doc={d.doc} />}
    </Card>
  );
}

/** Loading wrapper for the document tables. */
export function Docs({ state, children }: { state: ReturnType<typeof useDocs>; children: (docs: DocModel[]) => ReactNode }) {
  return <LoadState state={state}>{children}</LoadState>;
}

const CMD_IN_TEXT = /\b([a-z]+\.[a-z][a-z-]*)\b/;
const RUN_IN_TEXT = /\(?(yvc [^)]+)\)?/;
/** The command a gap sentence names ("… (walkthrough.record)", "… (yvc conclusion record --topic opinion)"), if the catalogue has it. */
export function commandIn(text: string, known: (c: string) => boolean): { command: string; run: string | null } | null {
  const run = text.match(RUN_IN_TEXT)?.[1] ?? null;
  if (run) {
    const [, a, b] = run.split(/\s+/);
    const c = a && b ? `${a}.${b}` : "";
    if (known(c)) return { command: c, run };
  }
  const m = text.match(CMD_IN_TEXT);
  return m && known(m[1]) ? { command: m[1], run: null } : null;
}

/** The engine's gap sentences, each with the command it names (the form opens with what the sentence's run line fixes). */
export function GapList({ gaps, open, title = "What is missing" }: { gaps: string[]; open: Open; title?: string }) {
  const { catalogue } = useApp();
  if (!gaps.length) return <p className="text-sm text-muted">Nothing missing in the engine's check.</p>;
  return (
    <div>
      <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted">{title}</h3>
      <ul className="divide-y divide-line">
        {gaps.map((g, i) => {
          const c = commandIn(g, (x) => catalogue.has(x));
          return (
            <li key={i} className="flex flex-wrap items-start gap-2 py-1.5 text-sm">
              <span className="min-w-0 flex-1">{g}</span>
              {c && <Act command={c.command} title={g} open={open} fixed={payloadFromRun(c.run, catalogue.get(c.command))} label={`${c.command}…`} />}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** A small key/value grid of engine figures shown verbatim. */
export function Facts({ items }: { items: Array<[string, ReactNode]> }) {
  return (
    <dl className="grid grid-cols-[minmax(6rem,max-content)_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
      {items.map(([k, v]) => <div key={k} className="contents"><dt className="text-muted">{k}</dt><dd className="min-w-0 break-words">{v ?? <span className="italic text-muted">not recorded</span>}</dd></div>)}
    </dl>
  );
}

export const asList = (v: unknown): Array<Record<string, unknown>> => (Array.isArray(v) ? v.filter((x) => x && typeof x === "object") as Array<Record<string, unknown>> : []);
export const asStrs = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => (typeof x === "string" ? x : JSON.stringify(x))) : []);
export const val = (v: unknown): ReactNode => (v === null || v === undefined || v === "" ? <span className="italic text-muted">—</span> : typeof v === "object" ? JSON.stringify(v) : String(v));

/** A read command asked of the engine through the inbox (mode "read"): the result lands in `inbox_results` when the engine has
 * run it. Used for heavy reads on demand (a full run trace). The demo never invents a result: it stays waiting. */
export function useReadRequest() {
  const { ds } = useApp();
  const send = useSend();
  const [state, setState] = useState<{ id: string | null; status: string | null; result: unknown; error: string | null }>({ id: null, status: null, result: null, error: null });
  useRealtime(ds, "inbox", (e) => {
    if (!e.row || String(e.row.id) !== state.id) return;
    const status = String(e.row.status ?? "");
    if (status === "done") void ds.readResult(state.id!).then((r) => setState((s) => ({ ...s, status, result: r?.result ?? (e.row?.result as { result?: unknown } | null)?.result ?? null })));
    else if (status === "refused" || status === "failed") setState((s) => ({ ...s, status, error: String((e.row?.result as Record<string, unknown> | null)?.[status] ?? status) }));
    else setState((s) => ({ ...s, status }));
  });
  const ask = async (command: string, payload: Record<string, unknown>, what: string) => {
    const row = await send(command, payload, what);
    if (row) setState({ id: row.id, status: row.status, result: null, error: null });
  };
  return { ...state, ask };
}

const GAP_KEYS = new Set(["gaps", "problems", "blockers", "missing"]);
/** A read-model document laid out for reading: scalar fields as facts, lists of records as tables, gap sentences with the
 * command they name. Values verbatim; nothing derived. */
export function SmartDoc({ doc, open, skip = [] }: { doc: Record<string, unknown>; open: Open; skip?: string[] }) {
  const entries = Object.entries(doc).filter(([k]) => !skip.includes(k));
  const scalars = entries.filter(([, v]) => v === null || typeof v !== "object");
  const rest = entries.filter(([, v]) => v !== null && typeof v === "object");
  return (
    <div className="space-y-3">
      {scalars.length > 0 && <Facts items={scalars.map(([k, v]) => [titleCase(k), val(v)])} />}
      {rest.map(([k, v]) => {
        if (Array.isArray(v) && GAP_KEYS.has(k)) return <GapList key={k} gaps={asStrs(v)} open={open} title={titleCase(k)} />;
        if (Array.isArray(v) && v.length && v.every((x) => x && typeof x === "object" && !Array.isArray(x))) return <RecordTable key={k} title={titleCase(k)} rows={v as Array<Record<string, unknown>>} />;
        if (Array.isArray(v)) return <div key={k}><h3 className="text-xs font-medium uppercase tracking-wide text-muted">{titleCase(k)}</h3>{v.length ? <DocView doc={v} /> : <p className="text-sm text-muted">None recorded.</p>}</div>;
        return <div key={k}><h3 className="text-xs font-medium uppercase tracking-wide text-muted">{titleCase(k)}</h3><DocView doc={v} /></div>;
      })}
    </div>
  );
}

/** A list of published records as a compact table (the first columns the records carry). */
export function RecordTable({ title, rows, columns }: { title?: string; rows: Array<Record<string, unknown>>; columns?: string[] }) {
  const cols = columns ?? [...new Set(rows.flatMap((r) => Object.keys(r)))].filter((k) => rows.some((r) => r[k] !== null && r[k] !== undefined && r[k] !== "")).slice(0, 7);
  return (
    <div className="min-w-0">
      {title && <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted">{title} · {rows.length}</h3>}
      <div className="relative overflow-x-auto rounded border border-line">
        <table className="w-full text-xs">
          {title && <caption className="sr-only">{title}</caption>}
          <thead className="bg-sunken text-left text-muted"><tr>{cols.map((c) => <th key={c} scope="col" className="px-2 py-1 font-medium">{titleCase(c)}</th>)}</tr></thead>
          <tbody className="divide-y divide-line">{rows.map((r, i) => <tr key={i}>{cols.map((c) => <td key={c} className="max-w-[28ch] break-words px-2 py-1 align-top">{val(r[c])}</td>)}</tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}
